import {DetailedError, parseResponse} from 'hono/client';
import {err, ok} from 'framework';
import type {Page, Result} from 'framework';
import type {RpcClient} from '@/infrastructure/rpc/rpc-client';
import type {
  CreateCustomExerciseInput,
  ExerciseCatalogRepository,
  ListCatalogExercisesInput,
  UpdateCustomExerciseInput,
} from '../../application/ports/exercise-catalog.repository';
import {
  CatalogExerciseNotFoundError,
  ExerciseNameConflictError,
  InvalidWorkoutInputError,
  NetworkError,
  ServerError,
  UnauthorizedError,
  type WorkoutError,
} from '../../domain/errors';
import type {CatalogExercise} from '../../domain/catalog-exercise';
import type {CatalogMuscle} from '../../domain/catalog-muscle';
import type {ExerciseId} from '../../domain/exercise-id.value-object';
import type {CatalogExerciseDtoToCatalogExerciseMapper} from '../mappers/catalog-exercise-dto-to-catalog-exercise.mapper';
import type {CatalogMuscleDtoToCatalogMuscleMapper} from '../mappers/catalog-muscle-dto-to-catalog-muscle.mapper';

export class RpcExerciseCatalogRepository implements ExerciseCatalogRepository {
  constructor(
    private readonly rpcClient: RpcClient,
    private readonly exerciseMapper: CatalogExerciseDtoToCatalogExerciseMapper,
    private readonly muscleMapper: CatalogMuscleDtoToCatalogMuscleMapper,
  ) {}

  async list(
    input: ListCatalogExercisesInput,
  ): Promise<Result<Page<CatalogExercise>, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.exercises.$get({
          query: {
            source: input.source,
            ...(input.cursor !== null && {cursor: input.cursor}),
            ...(input.query !== undefined && input.query.length > 0 && {q: input.query}),
          },
        }),
      );

      if ('error' in dto || dto.meta === undefined) {
        return err(new ServerError());
      }

      return ok({
        items: dto.data.map(item => this.exerciseMapper.transform(item)),
        nextCursor: dto.meta.nextCursor,
        hasMore: dto.meta.hasMore,
      });
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async getById(id: ExerciseId): Promise<Result<CatalogExercise, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.exercises[':exerciseId'].$get({
          param: {exerciseId: id},
        }),
      );
      return ok(this.exerciseMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async listMuscles(): Promise<Result<readonly CatalogMuscle[], WorkoutError>> {
    try {
      const dto = await parseResponse(this.rpcClient.exercises.muscles.$get());

      if ('error' in dto) {
        return err(new ServerError());
      }

      return ok(dto.data.map(item => this.muscleMapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async createCustom(
    input: CreateCustomExerciseInput,
  ): Promise<Result<CatalogExercise, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.exercises.$post({
          json: this.toWriteJson(input),
        }),
      );

      return ok(this.exerciseMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async updateCustom(
    input: UpdateCustomExerciseInput,
  ): Promise<Result<CatalogExercise, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.exercises[':exerciseId'].$patch({
          param: {exerciseId: input.id},
          json: this.toWriteJson(input),
        }),
      );
      return ok(this.exerciseMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async deleteCustom(id: ExerciseId): Promise<Result<void, WorkoutError>> {
    try {
      await parseResponse(
        this.rpcClient.exercises[':exerciseId'].$delete({
          param: {exerciseId: id},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  private toWriteJson(input: CreateCustomExerciseInput) {
    return {
      name: input.name,
      description: input.description,
      equipment: input.equipment,
      primaryMuscleIds: [...input.primaryMuscleIds],
      secondaryMuscleIds: [...input.secondaryMuscleIds],
    };
  }

  private mapError(cause: unknown): Result<never, WorkoutError> {
    if (cause instanceof DetailedError) {
      if (cause.statusCode === 401) {
        return err(new UnauthorizedError());
      }
      if (cause.statusCode === 404) {
        return err(new CatalogExerciseNotFoundError());
      }
      if (cause.statusCode === 409) {
        return err(new ExerciseNameConflictError());
      }
      if (cause.statusCode === 400 || cause.statusCode === 422) {
        return err(new InvalidWorkoutInputError());
      }
      return err(new ServerError());
    }
    return err(new NetworkError());
  }
}
